import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ShieldCheck, Play, Copy, Trash2, Plus, Sparkles, ExternalLink,
  Loader2, ChevronRight, ClipboardList, Check as CheckIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { WorkspaceNav } from "@/components/WorkspaceNav";
import { EvidenceTrigger } from "@/components/evidence/EvidencePanel";
import { CHECKS } from "@/constants/testIds";
import { useWorkspace } from "@/context/WorkspaceContext";
import api from "@/lib/api";

const SEMA_PRIMARY = "#1F3A93";

function StatusPill({ status }) {
  const map = {
    pass: { label: "PASS", cls: "bg-emerald-500/15 text-emerald-500 border-emerald-500/30" },
    fail: { label: "FAIL", cls: "bg-red-500/15 text-red-500 border-red-500/30" },
    error: { label: "ERROR", cls: "bg-amber-500/15 text-amber-500 border-amber-500/30" },
  };
  const cfg = map[status] || map.error;
  return (
    <Badge variant="outline" className={`rounded-full font-mono text-[10px] uppercase tracking-[0.14em] ${cfg.cls}`}>
      {cfg.label}
    </Badge>
  );
}

function CheckDialog({ open, onOpenChange, workspaceId, sets, onCreated }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [question, setQuestion] = useState("");
  const [sql, setSql] = useState("");
  const [citation, setCitation] = useState("");
  const [setId, setSetId] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setName(""); setDescription(""); setQuestion(""); setSql(""); setCitation(""); setSetId("");
  };

  const submit = async () => {
    if (!name.trim() || !sql.trim()) {
      toast.error("Name and SQL are required.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/checks", {
        workspace_id: workspaceId,
        name: name.trim(),
        description: description.trim(),
        question: question.trim(),
        sql: sql.trim(),
        threshold_op: "any",
        citation: citation.trim(),
        check_set_id: setId || null,
      });
      toast.success(`Created check: ${data.name}`);
      onCreated(data);
      reset();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create check.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent
        data-testid={CHECKS.dialog}
        className="max-w-lg"
      >
        <DialogHeader>
          <DialogTitle className="font-mono text-sm">New governed check</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-[0.14em] text-muted-foreground">Name</Label>
            <Input
              data-testid={CHECKS.dialogName}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Care-plan currency (F656)"
              className="mt-1"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.14em] text-muted-foreground">Description</Label>
            <Input
              data-testid={CHECKS.dialogDescription}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What this check enforces"
              className="mt-1"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.14em] text-muted-foreground">Question (NL)</Label>
            <Input
              data-testid={CHECKS.dialogQuestion}
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="Which residents have a stale care plan?"
              className="mt-1"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.14em] text-muted-foreground">
              SQL (returns failing records)
            </Label>
            <Textarea
              data-testid={CHECKS.dialogSql}
              value={sql}
              onChange={(e) => setSql(e.target.value)}
              placeholder="SELECT ... FROM ... WHERE ..."
              className="mt-1 h-40 font-mono text-xs"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-[0.14em] text-muted-foreground">Citation</Label>
              <Input
                data-testid={CHECKS.dialogCitation}
                value={citation}
                onChange={(e) => setCitation(e.target.value)}
                placeholder="CMS F656"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.14em] text-muted-foreground">Check Set</Label>
              <select
                value={setId}
                onChange={(e) => setSetId(e.target.value)}
                className="mt-1 h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="">— none —</option>
                {sets.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            data-testid={CHECKS.dialogSubmit}
            disabled={busy}
            onClick={submit}
            style={{ background: SEMA_PRIMARY }}
            className="text-white hover:opacity-95"
          >
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Create check
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RunResult({ run }) {
  if (!run) return null;
  return (
    <div data-testid={CHECKS.runResult} className="mt-2 rounded-md border border-border/60 p-3">
      <div className="flex items-center gap-2 text-xs">
        <StatusPill status={run.status} />
        <span className="text-muted-foreground">
          {run.failing} failing · {run.row_count} rows · v{run.check_version}
        </span>
      </div>
      {run.error && <div className="mt-2 text-xs text-red-500">{run.error}</div>}
      {(run.findings || []).length > 0 && (
        <ul className="mt-2 max-h-56 space-y-1 overflow-auto">
          {run.findings.slice(0, 25).map((f, i) => (
            <li
              key={i}
              data-testid={CHECKS.finding}
              className="flex items-center gap-2 rounded border border-border/40 px-2 py-1 text-[11px]"
            >
              <span className="flex-1 truncate font-mono text-muted-foreground">
                {Object.entries(f.record || {}).slice(0, 4).map(([k, v]) => `${k}=${v}`).join(", ")}
              </span>
              <EvidenceTrigger answerId={f.evidence_answer_id} label="Evidence" />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function Checks() {
  const { active } = useWorkspace();
  const workspaceId = active?.id;
  const [loading, setLoading] = useState(true);
  const [sets, setSets] = useState([]);
  const [checks, setChecks] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [runByCheck, setRunByCheck] = useState({});
  const [busyCheck, setBusyCheck] = useState(null);
  const [seeding, setSeeding] = useState(null);

  const load = async () => {
    if (!workspaceId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/checks/library`, { params: { workspace_id: workspaceId } });
      setSets(data.sets || []);
      setChecks(data.checks || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load check library.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [workspaceId]);

  const [packs, setPacks] = useState([]);
  useEffect(() => {
    let cancelled = false;
    api.get("/checks/packs").then(({ data }) => {
      if (!cancelled) setPacks(data.packs || []);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const bySet = useMemo(() => {
    const map = {};
    for (const s of sets) map[s.id] = { set: s, checks: [] };
    map[""] = { set: { id: "", name: "Unassigned", description: "" }, checks: [] };
    for (const c of checks) {
      const k = c.check_set_id || "";
      if (!map[k]) map[k] = { set: { id: k, name: "Unassigned" }, checks: [] };
      map[k].checks.push(c);
    }
    return Object.values(map).filter((g) => g.checks.length > 0 || g.set.id);
  }, [sets, checks]);

  const installedSlugs = useMemo(() => new Set(sets.map((s) => s.slug).filter(Boolean)), [sets]);

  const seed = async (packKey) => {
    setSeeding(packKey);
    try {
      const { data } = await api.post(`/checks/seed/${packKey}`, null, {
        params: { workspace_id: workspaceId },
      });
      toast.success(`Seeded ${data.checks?.length || 0} check(s).`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Seed failed.");
    } finally {
      setSeeding(null);
    }
  };

  const runOne = async (checkId) => {
    setBusyCheck(checkId);
    try {
      const { data } = await api.post(`/checks/${checkId}/run`);
      setRunByCheck((m) => ({ ...m, [checkId]: data }));
      toast[data.status === "pass" ? "success" : "warning"](
        `${data.check_name}: ${data.status.toUpperCase()} (${data.failing} findings)`,
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Run failed.");
    } finally {
      setBusyCheck(null);
    }
  };

  const runSet = async (setId) => {
    setBusyCheck(`set:${setId}`);
    try {
      const { data } = await api.post(`/checks/sets/${setId}/run`);
      const nextRuns = { ...runByCheck };
      for (const r of data.results || []) {
        if (r.check_id) nextRuns[r.check_id] = r;
      }
      setRunByCheck(nextRuns);
      const failing = (data.results || []).reduce((n, r) => n + (r.failing || 0), 0);
      toast.info(`Ran ${data.results?.length || 0} checks · ${failing} total finding(s).`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Run set failed.");
    } finally {
      setBusyCheck(null);
    }
  };

  const duplicate = async (checkId) => {
    try {
      const { data } = await api.post(`/checks/${checkId}/duplicate`);
      toast.success(`Duplicated as "${data.name}"`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Duplicate failed.");
    }
  };

  const remove = async (checkId) => {
    if (!confirm("Delete this check?")) return;
    try {
      await api.delete(`/checks/${checkId}`);
      toast.success("Deleted.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed.");
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid={CHECKS.root}>
      <WorkspaceNav />
      <main className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <div
              className="mb-1 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.16em]"
              style={{ color: SEMA_PRIMARY }}
            >
              <ShieldCheck className="h-3.5 w-3.5" /> Governed Checks
            </div>
            <h1 className="text-3xl font-semibold">Check Library</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Reusable, versioned, citation-linked data-quality checks. Every finding is signed as
              a replayable evidence pack.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  data-testid={CHECKS.seedBtn}
                  variant="outline"
                  disabled={!!seeding}
                  className="rounded-full text-xs"
                >
                  {seeding ? (
                    <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Sparkles className="mr-1.5 h-3.5 w-3.5" />
                  )}
                  Seed pack
                  <ChevronRight className="ml-1 h-3 w-3 rotate-90" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-72">
                <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
                  Bundled Check Sets
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {packs.map((p) => {
                  const installed = installedSlugs.has(p.slug);
                  return (
                    <DropdownMenuItem
                      key={p.key}
                      data-testid={`checks-seed-pack-${p.key}`}
                      onSelect={(e) => { e.preventDefault(); if (!installed) seed(p.key); }}
                      disabled={installed}
                      className="flex flex-col items-start gap-0.5 py-2"
                    >
                      <div className="flex w-full items-center gap-2">
                        <span className="text-xs font-medium">{p.name}</span>
                        <span className="ml-auto text-[10px] text-muted-foreground">
                          {p.num_checks} checks
                        </span>
                        {installed && <CheckIcon className="ml-1 h-3 w-3 text-emerald-500" />}
                      </div>
                      {p.description && (
                        <span className="text-[10px] text-muted-foreground line-clamp-2">
                          {p.description}
                        </span>
                      )}
                    </DropdownMenuItem>
                  );
                })}
                {packs.length === 0 && (
                  <DropdownMenuItem disabled>Loading packs…</DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              data-testid={CHECKS.newCheckBtn}
              onClick={() => setDialogOpen(true)}
              className="rounded-full text-xs text-white hover:opacity-95"
              style={{ background: SEMA_PRIMARY }}
            >
              <Plus className="mr-1.5 h-3.5 w-3.5" /> New check
            </Button>
            <Link
              to={workspaceId ? `/survey-readiness` : "#"}
              className="inline-flex items-center gap-1.5 rounded-full border border-border/60 px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground"
            >
              <ClipboardList className="h-3.5 w-3.5" /> Open Audit dashboard
              <ChevronRight className="h-3 w-3" />
            </Link>
          </div>
        </div>

        {loading ? (
          <div className="mt-10 flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading library…
          </div>
        ) : bySet.length === 0 ? (
          <div className="mt-16 rounded-2xl border border-dashed border-border/60 p-10 text-center">
            <ShieldCheck className="mx-auto mb-3 h-8 w-8" style={{ color: SEMA_PRIMARY }} />
            <p className="text-sm text-foreground/90">No checks yet.</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Use <em>Seed pack</em> to install a bundled Check Set (Senior Living, DPDP, BFSI, SaaS Metrics), or create your own.
            </p>
          </div>
        ) : (
          <div className="mt-6 space-y-8">
            {bySet.map((group) => (
              <section
                key={group.set.id || "unassigned"}
                data-testid={CHECKS.setCard}
                className="rounded-2xl border border-border/60 bg-background/50 p-5"
              >
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <h2 className="text-lg font-semibold">{group.set.name}</h2>
                    {group.set.description && (
                      <p className="text-xs text-muted-foreground">{group.set.description}</p>
                    )}
                  </div>
                  {group.set.id && (
                    <Button
                      data-testid={CHECKS.runSetBtn}
                      size="sm"
                      disabled={busyCheck === `set:${group.set.id}`}
                      onClick={() => runSet(group.set.id)}
                      className="rounded-full text-xs text-white hover:opacity-95"
                      style={{ background: SEMA_PRIMARY }}
                    >
                      {busyCheck === `set:${group.set.id}` ? (
                        <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Play className="mr-1.5 h-3.5 w-3.5" />
                      )}
                      Run set
                    </Button>
                  )}
                </div>
                <ul className="mt-4 space-y-3">
                  {group.checks.map((c) => (
                    <li
                      key={c.id}
                      data-testid={CHECKS.row}
                      className="rounded-lg border border-border/40 bg-background/60 p-3"
                    >
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{c.name}</span>
                            {c.citation && (
                              <Badge
                                variant="outline"
                                className="rounded-full font-mono text-[10px] uppercase tracking-[0.14em]"
                                style={{ borderColor: "rgba(31,58,147,0.4)", color: SEMA_PRIMARY }}
                              >
                                {c.citation}
                              </Badge>
                            )}
                            <span className="text-[10px] text-muted-foreground">v{c.version}</span>
                          </div>
                          {c.description && (
                            <p className="mt-1 text-xs text-muted-foreground">{c.description}</p>
                          )}
                          {c.question && (
                            <p className="mt-1 text-[11px] italic text-foreground/70">
                              “{c.question}”
                            </p>
                          )}
                        </div>
                        <div className="flex shrink-0 items-center gap-2">
                          <Button
                            data-testid={CHECKS.runBtn}
                            size="sm"
                            variant="outline"
                            disabled={busyCheck === c.id}
                            onClick={() => runOne(c.id)}
                            className="rounded-full text-[11px]"
                          >
                            {busyCheck === c.id ? (
                              <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <Play className="mr-1.5 h-3.5 w-3.5" />
                            )}
                            Run
                          </Button>
                          <Button
                            data-testid={CHECKS.duplicateBtn}
                            size="sm"
                            variant="ghost"
                            onClick={() => duplicate(c.id)}
                            className="rounded-full text-[11px] text-muted-foreground"
                          >
                            <Copy className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            data-testid={CHECKS.deleteBtn}
                            size="sm"
                            variant="ghost"
                            onClick={() => remove(c.id)}
                            className="rounded-full text-[11px] text-muted-foreground hover:text-red-500"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                      <RunResult run={runByCheck[c.id]} />
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        )}
      </main>
      <CheckDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        workspaceId={workspaceId}
        sets={sets}
        onCreated={() => load()}
      />
    </div>
  );
}
